import type { HiringMetricKey, MetricRowKey } from "../types";

/**
 * Every choice a manager makes on this page is THEIRS — the PRD is explicit that one user's edits must
 * never change what another user sees. So each key carries the user id by construction, and the
 * department id, because the same manager thinks about two departments differently.
 *
 * `-{jobId}` is added ONLY where the stored value is meaningless under another job. Per key:
 *   • census override, metric Count levers, detailed view, breakdown rows, grid open — DEPARTMENT.
 *     They are how this manager reads the department's gaps; none of them names a job, and re-picking
 *     the shape of the table every time you switch job pill would be a worse experience, not a safer one.
 *   • selected job — DEPARTMENT. The value IS the job, so scoping it by job is circular.
 *   • shift filter — JOB. Its values are shift LABELS drawn from the selected job's own templates, so
 *     the same stored list under a different job filters to labels that job may not even have.
 *
 * Deliberately kept next to (not merged into) `suggestedHiresStorageKey` / `hireSettingsStorageKey` /
 * `moveRequestsStorageKey`, which already ship and are read by their own hooks. Same naming shape.
 */
export type HiringStorageScope = {
	userId: string;
	departmentId: string;
	/** The job whose preferences these are. Only the job-scoped keys read it. */
	jobId: string;
};

export const censusOverrideStorageKey = (userId: string, departmentId: string): string => `hiring-census-override-${userId}-${departmentId}`;

export const metricFlagsStorageKey = (userId: string, departmentId: string): string => `hiring-metric-flags-${userId}-${departmentId}`;

export const selectedJobStorageKey = (userId: string, departmentId: string): string => `hiring-selected-job-${userId}-${departmentId}`;

export const gapsDetailedViewStorageKey = (userId: string, departmentId: string): string => `hiring-gaps-detailed-view-${userId}-${departmentId}`;

export const gapsShowFlagsStorageKey = (userId: string, departmentId: string): string => `hiring-gaps-show-flags-${userId}-${departmentId}`;

export const gapsOpenStorageKey = (userId: string, departmentId: string): string => `hiring-gaps-open-${userId}-${departmentId}`;

/**
 * The Hire tab's shift filter — the SHIFTS group in its Settings.
 *
 * Job-scoped: the stored values are shift LABELS from the selected job's own templates, and the same
 * list under another job would name labels that job may not run.
 *
 * The key string still says `gaps` because that is where this filter started life (inside the Recurring
 * Gaps table) and renaming it would drop every user's stored filter for nothing. The builder is named
 * for what it actually scopes now.
 */
export const hireShiftFilterStorageKey = (userId: string, departmentId: string, jobId: string): string =>
	`hiring-gaps-shift-filter-${userId}-${departmentId}-${jobId}`;

/**
 * The Move tab's shift filter — SEPARATE from the Hire tab's on purpose.
 *
 * The two boards answer different questions and get scoped independently: narrowing Hire to nights
 * because that is what you are recruiting for must not also hide every daytime move you could make
 * instead. Same job scoping and same "empty means no filter" convention as the Hire key.
 */
export const moveShiftFilterStorageKey = (userId: string, departmentId: string, jobId: string): string =>
	`hiring-move-shift-filter-${userId}-${departmentId}-${jobId}`;

/**
 * Which metrics count toward Missing (and therefore drive the server-recomputed recommendations).
 * Defaults per DAT-248: Structural OT + Internal Pickup non-OT on; Internal Pickup OT + Absences off.
 * (Agency is a display-only row, not a count lever.)
 *
 * Module-level so the identity is stable across renders — it is both a `useLocalStorageState` initial
 * value and the thing a "nothing stored yet" test asserts against.
 */
export const DEFAULT_INCLUDE_FLAGS: Record<HiringMetricKey, boolean> = {
	structural_ot: true,
	internal_pickup_ot: false,
	internal_pickup_non_ot: true,
	absences: false,
};

/** Which breakdown rows render in Detailed View — presentation only, all on until the user hides one. */
export const DEFAULT_SHOW_FLAGS: Record<MetricRowKey, boolean> = {
	structural_ot: true,
	internal_pickup_ot: true,
	internal_pickup_non_ot: true,
	agency: true,
	absences: true,
};

/** Nothing filtered = every shift label shows. Stored as an array because a `Set` has no JSON form. */
export const DEFAULT_SHIFT_FILTER: string[] = [];

/** The grid starts expanded; the stored value is what the user last left it as. */
export const DEFAULT_GAPS_OPEN = true;

/** Detailed View starts collapsed: one "Missing" row per shift until the user asks for the breakdown. */
export const DEFAULT_DETAILED_VIEW = false;

/**
 * The shift-label filter that is actually in force, from what the user stored and what the job's
 * templates currently offer.
 *
 * Stored labels outlive the templates they came from — rename or drop a shift type and the stored list
 * names something that no longer exists. Filtering on it would leave the grid permanently empty, with
 * the filter chips that could clear it rendered INSIDE the grid it just emptied. So a label that has
 * gone away simply stops filtering: this is validation of stored data against reality, not a default
 * standing in for a missing value.
 */
export const activeShiftFilter = (storedLabels: string[], availableLabels: string[]): Set<string> => {
	const available = new Set(availableLabels);
	return new Set(storedLabels.filter((label) => available.has(label)));
};

/**
 * Which job the page is on. The URL param stays the source of truth whenever it is present, so a shared
 * link still lands the recipient on the job the sender meant; the stored preference only answers the
 * case the URL doesn't — a fresh visit with no param.
 */
export const resolveSelectedJobId = (urlJobId: string | null, storedJobId: string | null): string | undefined => urlJobId ?? storedJobId ?? undefined;
